import { useCallback, useEffect, useState } from "react";
import { CalSchedule, CalScheduleAvailability, CalDateOverride } from "@/types/calendar";
import { useCalApi } from "./useCalApi";

type SchedulesResponse = {
  schedules: CalSchedule[];
};

type UpdateScheduleParams = {
  id: number;
  name?: string;
  timeZone?: string;
  isDefault?: boolean;
  availability?: CalScheduleAvailability[];
  dateOverrides?: CalDateOverride[];
};

type UseCalendarSchedulesResult = {
  isLoading: boolean;
  schedules: CalSchedule[];
  defaultSchedule: CalSchedule | null;
  refetch: () => Promise<void>;
  updateSchedule: (params: UpdateScheduleParams) => Promise<boolean>;
  isUpdating: boolean;
};

/**
 * Hook to manage Cal.com schedules/availability.
 * Provides CRUD operations for user availability schedules.
 */
export function useCalendarSchedules(): UseCalendarSchedulesResult {
  const { fetchCalApi } = useCalApi();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [schedules, setSchedules] = useState<CalSchedule[]>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch the default schedule
      const result = await fetchCalApi<SchedulesResponse>("/schedules?default=true");
      setSchedules(result?.schedules || []);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCalApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSchedule = useCallback(
    async (params: UpdateScheduleParams): Promise<boolean> => {
      setIsUpdating(true);
      try {
        const result = await fetchCalApi<{ success: boolean; schedule: CalSchedule }>(
          "/schedules",
          {
            method: "PATCH",
            body: params,
          }
        );

        if (result?.success && result.schedule) {
          // Update local state
          setSchedules((prev) =>
            prev.map((s) => (s.id === params.id ? result.schedule : s))
          );
          return true;
        }
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchCalApi]
  );

  const defaultSchedule = schedules.find((s) => s.isDefault) || schedules[0] || null;

  return {
    isLoading,
    schedules,
    defaultSchedule,
    refetch: fetchData,
    updateSchedule,
    isUpdating,
  };
}
